import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { resolvePath, useNavigate } from 'react-router';
import { FullStory } from '@fullstory/browser';

import pages from 'pages';
import { MilestoneType, OnboardingStep } from 'modules/types/util';
import { useBreakpoints } from 'hooks/useWindowSize';
import { Text } from 'shared/components/Text/Text';
import { useTheme } from 'styled-components';
import { ArrowToBracketIcon } from 'shared/components/icons/ArrowToBracketIcon';
import { Button } from 'shared/components/Button/Button';
import { useGlobalState } from 'hooks/useGlobalState';
import { SupportIcon } from '../../icons/SupportIcon';
import { SolarPanelIcon } from '../../icons/SolarPanelIcon';
import { CheckIconBold } from '../../icons/CheckIconBold';
import { ClipboardIcon } from '../../icons/ClipboardIcon';
import { WrenchAndScrewdriverIcon } from '../../icons/WrenchAndScrewdriverIcon';
import { BoltLightIcon } from '../../icons/BoltLightIcon';
import {
  AddressDisplayWrapper,
  IconSpacer,
  OnboardingNavWrapper,
  Status,
} from './OnboardingNav.styles';
import { MenuButton } from '../../MenuButton/MenuButton';
import { StatusTrackerItem } from './StatusTrackerItem';
import { useOnboardingStep } from '../../../../hooks/useOnboardingStep';
import { useUser } from '../../../../hooks/useUser';
import { AddressType } from '../../../../api/types';
import { TDropdownDataItem } from '../../../design-system/theme/dropdowns';
import { CheckIcon } from '../../icons';
import { getLastCompletedStep } from './getLastCompletedStep';

/** An agreement should have access to the onboarding steps related to its
 * current milestone, regardless of onboarding milestone progress.
 */
const MINIMUM_ACCESSIBLE_STEP = {
  [MilestoneType.CONTRACT_SIGNED]: OnboardingStep.INTRODUCTION,
  [MilestoneType.PROJECT_QUALIFICATION_COMPLETED]: OnboardingStep.SYSTEM_INSTALLATION,
  [MilestoneType.INSTALLATION_COMPLETED]: OnboardingStep.SYSTEM_ACTIVATION,
  [MilestoneType.IN_SERVICE]: OnboardingStep.SYSTEM_ACTIVATION,
};

export function OnboardingNav({ onClick }: { onClick?: () => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation('translation', { keyPrefix: 'status.prePto' });
  const screenSize = useBreakpoints();
  const theme = useTheme();

  const { userResult, selectedAgreement } = useUser();
  const { setAccountMenuOpen } = useGlobalState();

  const { currentStep, goToStep, completedSubtasks, showConfirmAddressStep } = useOnboardingStep();

  const userAgreementAddressOptions: TDropdownDataItem[] = useMemo(() => {
    const optionsArray: { id: string; address: AddressType }[] = [];
    userResult?.user?.agreements.forEach((agreement) => {
      optionsArray.push({ id: agreement.id, address: agreement.system.address });
    });
    return optionsArray.map(({ id, address }) => {
      const addressString = t('addressFormat', {
        street_1: address.street_1,
        street_2: address.street_2 ? ` ${address.street_2}` : '',
        city: address.city,
      });
      return {
        title: addressString,
        value: id,
        Icon: selectedAgreement && id === selectedAgreement.id ? CheckIcon : () => <IconSpacer />,
      };
    });
  }, [userResult, t, selectedAgreement]);

  const visibleSteps: OnboardingStep[] = useMemo(() => {
    const arr: OnboardingStep[] = [
      OnboardingStep.INTRODUCTION,
      OnboardingStep.PRE_INSTALLATION,
      OnboardingStep.REVIEW_AGREEMENT,
      OnboardingStep.TPV,
      OnboardingStep.AUTOPAY,
      ...(showConfirmAddressStep ? [OnboardingStep.CONFIRM_ADDRESS] : []),
      OnboardingStep.SYSTEM_INSTALLATION,
      OnboardingStep.SYSTEM_ACTIVATION,
    ];
    return arr;
  }, [showConfirmAddressStep]);

  const isSubtaskSet = useMemo(
    () =>
      new Set<OnboardingStep>([
        OnboardingStep.REVIEW_AGREEMENT,
        OnboardingStep.TPV,
        OnboardingStep.AUTOPAY,
        ...(showConfirmAddressStep ? [OnboardingStep.CONFIRM_ADDRESS] : []),
      ]),
    [showConfirmAddressStep],
  );

  const isSubtask = useCallback((step: OnboardingStep) => isSubtaskSet.has(step), [isSubtaskSet]);

  const labelOf = useCallback(
    (step: OnboardingStep) => {
      switch (step) {
        case OnboardingStep.INTRODUCTION:
          return t('introduction');
        case OnboardingStep.PRE_INSTALLATION:
          return t('preInstallation');
        case OnboardingStep.REVIEW_AGREEMENT:
          return t('review');
        case OnboardingStep.TPV:
          return t('tpv');
        case OnboardingStep.AUTOPAY:
          return t('enroll');
        case OnboardingStep.CONFIRM_ADDRESS:
          return t('confirm');
        case OnboardingStep.SYSTEM_INSTALLATION:
          return t('systemInstallation');
        case OnboardingStep.SYSTEM_ACTIVATION:
          return t('systemActivation');
        default:
          return '';
      }
    },
    [t],
  );

  const getTaskIcon = useCallback((thisStep: OnboardingStep) => {
    const taskIcons = {
      [OnboardingStep.INTRODUCTION]: SolarPanelIcon,
      [OnboardingStep.PRE_INSTALLATION]: ClipboardIcon,
      [OnboardingStep.SYSTEM_INSTALLATION]: WrenchAndScrewdriverIcon,
      [OnboardingStep.SYSTEM_ACTIVATION]: BoltLightIcon,
      [OnboardingStep.REVIEW_AGREEMENT]: undefined,
      [OnboardingStep.TPV]: undefined,
      [OnboardingStep.AUTOPAY]: undefined,
      [OnboardingStep.CONFIRM_ADDRESS]: undefined,
    } as const;
    return taskIcons[thisStep] || CheckIconBold;
  }, []);

  const getTaskStatus = useCallback(
    (thisStep: OnboardingStep, lastCompletedStep: OnboardingStep | null) => {
      if (isSubtask(currentStep) && thisStep === OnboardingStep.PRE_INSTALLATION) {
        return lastCompletedStep && lastCompletedStep >= OnboardingStep.PRE_INSTALLATION
          ? Status.COMPLETED
          : Status.IN_PROGRESS;
      }

      // handle subtask status
      if (isSubtask(thisStep)) {
        if (completedSubtasks.has(thisStep)) return Status.COMPLETED;
        if (currentStep === thisStep) return Status.IN_PROGRESS;
        return currentStep > thisStep ? Status.HIGHLIGHTED : Status.INCOMPLETE;
      }

      if (currentStep === thisStep) {
        if (lastCompletedStep === null) return Status.IN_PROGRESS;
        return thisStep > lastCompletedStep ? Status.IN_PROGRESS : Status.COMPLETED;
      }

      return lastCompletedStep !== null && thisStep <= lastCompletedStep
        ? Status.COMPLETED
        : Status.INCOMPLETE;
    },
    [currentStep, completedSubtasks, isSubtask],
  );

  const getTaskDetail = useCallback(
    (thisStep: OnboardingStep, taskStatus: Status) => {
      if (taskStatus === Status.COMPLETED && !isSubtask(thisStep)) {
        return t('complete');
      }
      if (thisStep === OnboardingStep.PRE_INSTALLATION) {
        // show how many subtasks are completed among the (possibly reduced) subtask set
        const completedCount = Array.from(isSubtaskSet).reduce(
          (acc, step) => acc + (completedSubtasks.has(step) ? 1 : 0),
          0,
        );

        return t('subtasksComplete', {
          completed: completedCount,
          stepCount: Array.from(isSubtaskSet).length,
        });
      }
      if (thisStep === OnboardingStep.TPV && !completedSubtasks.has(OnboardingStep.TPV)) {
        return t('actionRequired');
      }
      return undefined;
    },
    [t, completedSubtasks, isSubtask, isSubtaskSet],
  );

  /** Don't allow users to click into steps past the one currently in-progress */
  const getTaskDisabledStatus = useCallback(
    (thisStep: OnboardingStep, lastCompletedStep: OnboardingStep | null) => {
      const currentMilestone = selectedAgreement?.current_milestone;
      if (lastCompletedStep === null) return thisStep > 0;
      // subtasks will never be disabled if lastCompletedStep is not null
      if (isSubtask(thisStep)) return false;
      // disable step if we haven't completed the previous non-subtask onboarding step...
      const isDisabledByOnboardingMilestone =
        thisStep === OnboardingStep.SYSTEM_INSTALLATION
          ? lastCompletedStep < OnboardingStep.PRE_INSTALLATION
          : lastCompletedStep < thisStep - 1;
      // ...unless the agreement itself has progressed past that step
      const isDisabledByAgreementMilestone =
        !!currentMilestone && MINIMUM_ACCESSIBLE_STEP[currentMilestone] < thisStep;

      return isDisabledByOnboardingMilestone && isDisabledByAgreementMilestone;
    },
    [isSubtask, selectedAgreement?.current_milestone],
  );

  const onboardingTasks = useMemo(() => {
    const lastCompletedStep = getLastCompletedStep(selectedAgreement?.onboarding_milestone || null);
    return visibleSteps.map((step) => {
      const status = getTaskStatus(step, lastCompletedStep);
      return {
        step,
        label: labelOf(step),
        status,
        detail: getTaskDetail(step, status),
        icon: getTaskIcon(step),
        disabled: getTaskDisabledStatus(step, lastCompletedStep),
      };
    });
  }, [
    visibleSteps,
    labelOf,
    getTaskStatus,
    getTaskDetail,
    getTaskIcon,
    getTaskDisabledStatus,
    selectedAgreement?.onboarding_milestone,
  ]);

  const getLastSubtask = useCallback(() => {
    const subtasks = Array.from(isSubtaskSet);
    return subtasks[subtasks.length - 1];
  }, [isSubtaskSet]);

  const selectedAddressString = useMemo(() => {
    const address = selectedAgreement?.system.address;
    if (!address) return '';
    return t('address', {
      keyPrefix: '',
      street_1: address.street_1,
      street_2: address.street_2 ? ` ${address.street_2}` : '',
      city: address.city,
    });
  }, [selectedAgreement, t]);

  return (
    <OnboardingNavWrapper>
      {selectedAgreement && userAgreementAddressOptions.length > 1 && (
        <AddressDisplayWrapper>
          <Text as="p" $lineClamp={1} color={theme.colors.textSecondaryGrey90} $fontWeight={700}>
            {selectedAddressString}
          </Text>
          <Button
            styleVariant="tertiary"
            Icon={ArrowToBracketIcon}
            maxHeight="24px"
            onClick={() => setAccountMenuOpen(true)}
          />
        </AddressDisplayWrapper>
      )}

      {onboardingTasks.map(({ label, status, detail, icon, step, disabled }, idx) => (
        <StatusTrackerItem
          key={label}
          label={label}
          status={status}
          Icon={icon}
          disabled={disabled}
          isSubtask={isSubtask(step)}
          showLine={idx !== onboardingTasks.length - 1}
          showLineCompleted={currentStep > step}
          detail={detail}
          margin={
            step === getLastSubtask() ? `0 0 ${screenSize === 'sm' ? '18px' : '24px'} 0` : undefined
          }
          onClick={() => {
            onClick?.();
            goToStep(step);
            FullStory('trackEvent', {
              name: `Onboarding - Navigation Clicked: ${labelOf(step)}`,
              properties: {
                page: 'Status',
                onboardingMilestone: selectedAgreement?.onboarding_milestone,
              },
            });
          }}
        />
      ))}

      {screenSize !== 'sm' && (
        <MenuButton
          key={t('support', { keyPrefix: 'overview' })}
          label={t('support', { keyPrefix: 'overview' })}
          onClick={() => navigate(resolvePath(`/${pages.SUPPORT}`))}
          Icon={SupportIcon}
          margin="auto 0 48px"
          fontSize="16px"
        />
      )}
    </OnboardingNavWrapper>
  );
}

export default OnboardingNav;
